import React, { Children, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { useDocument } from '@nandorojo/swr-firestore';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { SettingCollectionPath } from '@typings/collections.enum';
import { scrollbarHide } from '@styles/variants/scrollbar';

const directionMap = {
  grid1: css``,
  grid2: css`
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, 1fr);

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
  grid3: css`
    display: grid;
    gap: 16px 12px;
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
  `,
  grid4: css`
    display: grid;
    gap: 16px 24px;
    grid-template-columns: repeat(4, 1fr);

    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `,
  wrap: css`
    flex-direction: row;
    gap: 16px 24px;
  `,
  column: css`
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  `,
  columnReverse: css`
    flex-direction: column-reverse;
    align-items: flex-start;
  `,
  autofill: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(30%, 1fr));
    gap: 16px 12px;
    grid-auto-flow: row dense;
    grid-auto-rows: 10vh;
  `,
  masonry: css``, // Masonry 레이아웃은 하단 Container에서 별도 처리
};

const ItemDirMap = {
  grid3: css``,
  grid4: css``,
  wrap: css``,
  column: css`
    width: 100%;
  `,
  columnReverse: css`
    width: 100%;
  `,
  autofill: css``,
  masonry: css``,
};

const Container = styled(motion.div) <{
  direction?: keyof typeof directionMap;
  line: number;
  mobileLine: number;
  gapValue: string;
}>`
  position: relative;
  justify-content: center;

  ${scrollbarHide}
  ${({ direction }) => direction && directionMap[direction]}

  ${({ direction, line, mobileLine, gapValue }) =>
    direction === 'masonry'
      ? css`
          display: block;
          column-count: ${line};
          column-gap: ${gapValue};

          @media (max-width: 480px) {
            column-count: ${mobileLine};
          }
        `
      : css`
          display: grid;
          gap: ${gapValue};
          grid-template-columns: repeat(${line}, minmax(0, 1fr));

          @media (max-width: 480px) {
            grid-template-columns: repeat(${mobileLine}, minmax(0, 1fr));
          }
        `}

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Item = styled(motion.div) <{
  direction?: keyof typeof directionMap;
  gapValue?: string;
}>`
  ${({ direction }) => direction && ItemDirMap[direction]}

  @media (max-width: 480px) {
    width: 100%;
  }

  ${({ direction, gapValue }) =>
    direction === 'masonry' &&
    css`
      break-inside: avoid;
      margin-bottom: ${gapValue || '16px'};
      width: 100%;
    `}
`;

const listWrapVarinats = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const listVarinats = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0 },
};

type SHASHTAGgerMotionProps = {
  children: any;
  direction?: keyof typeof directionMap;
  style?: any;
  layout?: boolean;
  rows?: number;
  line?: number;
  mobileLine?: number;
  gap?: string; // 간격 px
};

function StaggerMotionWrapper({
  children,
  direction = 'wrap',
  line = 1,
  mobileLine = 1,
  style,
  layout,
  rows,
  gap,
}: SHASHTAGgerMotionProps) {
  const { data: homeSet } = useDocument<IhomepageSetting>(
    SettingCollectionPath.HOMEPAGE,
    { listen: false },
  );

  const gapValue = gap || '16px';

  return (
    <Container
      variants={homeSet?.pageEffect ? listWrapVarinats : {}}
      initial="hidden"
      animate="show"
      direction={direction}
      line={line}
      mobileLine={mobileLine}
      transition={{ ease: 'easeInOut' }}
      layout={layout}
      gapValue={gapValue}
      style={{ gap: gapValue, ...style }}
    >
      {children &&
        Children.map(children, (child, i) => {
          if (!child) return;
          return (
            <Item
              variants={homeSet?.pageEffect ? listVarinats : {}}
              key={i}
              direction={direction}
              gapValue={gapValue}
            >
              {child}
            </Item>
          );
        })}
    </Container>
  );
}

export default StaggerMotionWrapper;
